import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

// A simple decision-branch diagram: condition -> true path / false path
const BranchDiagram: React.FC<{ condition: string; truePath: string; falsePath: string }> = ({ condition, truePath, falsePath }) => (
 <div className="my-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 p-6">
  <div className="flex items-center justify-center gap-4 sm:gap-6 flex-wrap">
   <div className="px-4 py-2.5 rounded-lg border-2 border-amber-400 dark:border-amber-600 bg-white dark:bg-gray-900 text-sm font-semibold text-amber-700 dark:text-amber-300 text-center max-w-[200px]">{condition}</div>
   <div className="flex flex-col gap-3">
    <div className="flex items-center gap-2">
     <span className="text-[10px] font-bold uppercase text-green-600 dark:text-green-400">true</span>
     <span className="text-gray-300 dark:text-gray-600">→</span>
     <div className="px-3 py-2 rounded-lg border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-[13px] font-medium text-green-700 dark:text-green-300">{truePath}</div>
    </div>
    <div className="flex items-center gap-2">
     <span className="text-[10px] font-bold uppercase text-red-600 dark:text-red-400">false</span>
     <span className="text-gray-300 dark:text-gray-600">→</span>
     <div className="px-3 py-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-[13px] font-medium text-red-700 dark:text-red-300">{falsePath}</div>
    </div>
   </div>
  </div>
 </div>
);

export const JSControlFlow: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Control Flow</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    How a program chooses what to do next — making decisions, branching, and repeating actions instead of marching straight down the page.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="14 min read" />
  </div>

  {/* 1. Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>In a food delivery app, almost everything is a decision: if payment succeeds, create the order — else show a failure. If a partner is available, assign the order — else find another. If credentials are valid, log in — else reject.</Para>
   <Para>But by default, code just runs top to bottom, the same way every time:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log("Step 1");\nconsole.log("Step 2");\nconsole.log("Step 3");` }]} defaultLanguage="javascript" id="cf-linear" />
   <Output>{`Step 1\nStep 2\nStep 3`}</Output>
   <Para>No matter what happens, it always follows the same path. Real apps need <Mark>different paths for different conditions</Mark> — that&apos;s control flow.</Para>
  </section>

  {/* 2. Definition */}
  <section className="mb-12">
   <SectionHeading n={2}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200"><Mark>Control flow</Mark> determines the order in which statements execute — letting a program make decisions, repeat actions, skip code, and choose different execution paths.</p>
   </div>
  </section>

  {/* 3. Why it exists */}
  <section className="mb-12">
   <SectionHeading n={3}>Why It Exists</SectionHeading>
   <Para>Applications constantly branch: Can the user log in? Was payment successful? Is stock available? Should a discount apply? Did the API call succeed? Each needs branching logic — &quot;execute based on conditions&quot; rather than &quot;always execute everything.&quot;</Para>
  </section>

  {/* 4. Core idea */}
  <section className="mb-12">
   <SectionHeading n={4}>Core Idea</SectionHeading>
   <Para>Think of control flow as road navigation. Without it there&apos;s a single path to the destination. With it, the program can take different routes depending on what&apos;s true:</Para>
   <BranchDiagram condition="Payment successful?" truePath="Create order" falsePath="Show error" />
  </section>

  {/* 5/6. if */}
  <section className="mb-12">
   <SectionHeading n={5}>The if Statement</SectionHeading>
   <Para>The simplest decision — run a block only when a condition is true:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const age = 20;\n\nif (age >= 18) {\n  console.log("Eligible to vote");\n}` }]} defaultLanguage="javascript" id="cf-if" />
   <Output>Eligible to vote</Output>
   <Para>If the condition is false (say <Code>age = 15</Code>), the block is simply skipped and nothing prints.</Para>
  </section>

  {/* 7. if...else */}
  <section className="mb-12">
   <SectionHeading n={6}>if...else</SectionHeading>
   <Para>Two possible outcomes — exactly one branch runs:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const paymentSuccessful = false;\n\nif (paymentSuccessful) {\n  console.log("Order Created");\n} else {\n  console.log("Payment Failed");\n}` }]} defaultLanguage="javascript" id="cf-ifelse" />
   <Output>Payment Failed</Output>
  </section>

  {/* 8. else if */}
  <section className="mb-12">
   <SectionHeading n={7}>if...else if...else</SectionHeading>
   <Para>For multiple decisions. JavaScript checks each condition in order and stops at the first match:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const score = 82;\n\nif (score >= 90) {\n  console.log("Grade A");\n} else if (score >= 75) {\n  console.log("Grade B");\n} else if (score >= 60) {\n  console.log("Grade C");\n} else {\n  console.log("Grade D");\n}` }]} defaultLanguage="javascript" id="cf-elseif" />
   <Output>Grade B</Output>
   <Callout tone="info">Once a matching condition is found, the remaining conditions are skipped — order matters.</Callout>
  </section>

  {/* 9. Nested */}
  <section className="mb-12">
   <SectionHeading n={8}>Nested Conditions</SectionHeading>
   <Para>Conditions can live inside other conditions:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const isLoggedIn = true;\nconst isAdmin = true;\n\nif (isLoggedIn) {\n  if (isAdmin) {\n    console.log("Admin Dashboard");\n  }\n}` }]} defaultLanguage="javascript" id="cf-nested" />
   <Output>Admin Dashboard</Output>
   <Callout tone="warning" title="Pyramid of Doom">Too much nesting (if inside if inside if…) becomes hard to read. Flatten it with early returns or combined conditions.</Callout>
  </section>

  {/* 10. Ternary */}
  <section className="mb-12">
   <SectionHeading n={9}>Ternary Operator</SectionHeading>
   <Para>A shorthand for a simple if/else that produces a value:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const age = 20;\nconst status = age >= 18 ? "Adult" : "Minor";\nconsole.log(status);` }]} defaultLanguage="javascript" id="cf-ternary" />
   <Output>Adult</Output>
   <Callout tone="warning" title="Don't nest ternaries">Chains like <Code>a ? b ? c : d : e</Code> are unreadable. Use ternaries only for simple, single decisions.</Callout>
  </section>

  {/* 11. Switch */}
  <section className="mb-12">
   <SectionHeading n={10}>Switch Statement</SectionHeading>
   <Para>Best when comparing one value against many fixed cases:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const role = "admin";\n\nswitch (role) {\n  case "admin":\n    console.log("Admin Access");\n    break;\n  case "manager":\n    console.log("Manager Access");\n    break;\n  case "user":\n    console.log("User Access");\n    break;\n  default:\n    console.log("No Access");\n}` }]} defaultLanguage="javascript" id="cf-switch" />
   <Output>Admin Access</Output>
   <Callout tone="warning" title="Don't forget break">Without <Code>break</Code>, execution &quot;falls through&quot; into the next case — a common bug.</Callout>
  </section>

  {/* 12. Loops intro */}
  <section className="mb-12">
   <SectionHeading n={11}>Loops</SectionHeading>
   <Para>When you need to repeat an action — say, notify 100 users — writing one call per user is impractical. Loops repeat a block until a condition says stop.</Para>
  </section>

  {/* 13. for */}
  <section className="mb-12">
   <SectionHeading n={12}>for Loop</SectionHeading>
   <Para>The most common loop, used when you know how many times to run:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `for (let i = 1; i <= 5; i++) {\n  console.log(i);\n}` }]} defaultLanguage="javascript" id="cf-for" />
   <Output>{`1\n2\n3\n4\n5`}</Output>
  </section>

  {/* 14. while */}
  <section className="mb-12">
   <SectionHeading n={13}>while Loop</SectionHeading>
   <Para>Runs while a condition stays true — ideal when the number of iterations is unknown (e.g. &quot;keep retrying the API until it succeeds&quot;):</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `let count = 1;\nwhile (count <= 5) {\n  console.log(count);\n  count++;\n}` }]} defaultLanguage="javascript" id="cf-while" />
   <Output>{`1\n2\n3\n4\n5`}</Output>
  </section>

  {/* 15. do...while */}
  <section className="mb-12">
   <SectionHeading n={14}>do...while Loop</SectionHeading>
   <Para>Like <Code>while</Code>, but the body always runs at least once because the check happens after:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `let count = 1;\ndo {\n  console.log(count);\n  count++;\n} while (count <= 3);` }]} defaultLanguage="javascript" id="cf-dowhile" />
   <Output>{`1\n2\n3`}</Output>
   <div className="my-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
     <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">while</div>
     <div className="text-[13px] text-gray-600 dark:text-gray-400">Check first, then run — may run zero times.</div>
    </div>
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
     <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">do...while</div>
     <div className="text-[13px] text-gray-600 dark:text-gray-400">Run first, then check — always runs at least once.</div>
    </div>
   </div>
  </section>

  {/* 16. break / 17. continue */}
  <section className="mb-12">
   <SectionHeading n={15}>break &amp; continue</SectionHeading>
   <Para><Code>break</Code> stops the loop entirely; <Code>continue</Code> skips just the current iteration.</Para>
   <SubHeading>break — stop the loop</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `for (let i = 1; i <= 10; i++) {\n  if (i === 5) break;\n  console.log(i);\n}` }]} defaultLanguage="javascript" id="cf-break" />
   <Output>{`1\n2\n3\n4`}</Output>
   <SubHeading>continue — skip this iteration</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `for (let i = 1; i <= 5; i++) {\n  if (i === 3) continue;\n  console.log(i);\n}` }]} defaultLanguage="javascript" id="cf-continue" />
   <Output>{`1\n2\n4\n5`}</Output>
  </section>

  {/* 18. Real world */}
  <section className="mb-12">
   <SectionHeading n={16}>Real-World Example</SectionHeading>
   <Para>An order-processing decision combining comparison and logical operators:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const orderAmount = 1500;\nconst isPremiumUser = true;\n\nif (orderAmount > 1000 && isPremiumUser) {\n  console.log("Apply Premium Discount");\n} else {\n  console.log("No Discount");\n}` }]} defaultLanguage="javascript" id="cf-realworld" />
   <Output>Apply Premium Discount</Output>
  </section>

  {/* 19. Common mistakes */}
  <section className="mb-12">
   <SectionHeading n={17}>Common Mistakes</SectionHeading>
   <Callout tone="warning" title="Assignment instead of comparison"><Code>if (age = 18)</Code> assigns; you want <Code>if (age === 18)</Code>.</Callout>
   <Callout tone="warning" title="Forgetting break in switch">Missing <Code>break</Code> causes fall-through into the next case.</Callout>
   <Callout tone="warning" title="Infinite loops"><Code>while (true) { }</Code> with no exit will freeze the app — make sure the condition can become false.</Callout>
   <Callout tone="warning" title="Deep nesting">Pyramids of nested ifs are hard to maintain; flatten them.</Callout>
  </section>

  {/* 20. Interview concepts */}
  <section className="mb-12">
   <SectionHeading n={18}>Interview Concepts</SectionHeading>
   <div className="flex flex-wrap gap-2">
    {['if vs switch', 'while vs do...while', 'When to use a for loop', 'What is an infinite loop?', 'break vs continue', 'Can switch replace if...else?'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
  </section>

  {/* 21. Mental model */}
  <section className="mb-12">
   <SectionHeading n={19}>Mental Model</SectionHeading>
   <Para>Think of control flow as traffic signals telling the program where to go next:</Para>
   <div className="my-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
    {[
     { c: 'Green', m: 'Proceed', cls: 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' },
     { c: 'Red', m: 'Stop', cls: 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300' },
     { c: 'Yellow', m: 'Wait / decide', cls: 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300' },
    ].map((s) => (
     <div key={s.c} className={`rounded-xl border p-4 text-center ${s.cls}`}>
      <div className="text-sm font-bold">{s.c}</div>
      <div className="text-[13px] opacity-80">{s.m}</div>
     </div>
    ))}
   </div>
  </section>

  {/* 22 & 23 */}
  <section className="mb-4">
   <SectionHeading n={20}>Interview One-Liner &amp; Takeaway</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Interview One-Liner</div>
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     Control flow determines the execution path of a program by allowing decisions, branching, repetition, and conditional execution.
    </p>
   </div>
   <Callout tone="info" title="Most important takeaway">
    Variables store data, operators manipulate it, and control flow decides what to do with it. Control flow is what turns a list of statements into a real application that can make decisions.
   </Callout>
  </section>
 </div>
);

export default JSControlFlow;
